import { Heart } from 'lucide-react';
import Colors from '@/constants/colors';
import React from 'react';
import { useAppStore } from '@/lib/store';

type GridSize = '1x1' | '2x1' | '3x1' | '1x2' | '2x2' | '3x2';

interface GridTileProps {
  id: string;
  imageUrl: string;
  imageAlt: string;
  title: string;
  size: GridSize;
  onCardClick?: () => void;
  showFavoriteButton?: boolean;
}

export default function GridTile({
  id,
  imageUrl,
  imageAlt,
  title,
  size,
  onCardClick,
  showFavoriteButton = true,
}: GridTileProps) {
  const isFavorite = useAppStore((state) => state.isFavorite(id));
  const toggleFavorite = useAppStore((state) => state.toggleFavorite);

  const sizeStyles = getSizeStyles(size);

  return (
    <div
      style={{
        ...styles.tile,
        ...sizeStyles,
        cursor: onCardClick ? 'pointer' : 'default',
      }}
      onClick={onCardClick}
    >
      <img
        src={imageUrl}
        alt={imageAlt}
        style={styles.image}
        loading="lazy"
        decoding="async"
      />
      <div style={styles.overlay}>
        <h4 style={styles.title}>{title}</h4>
      </div>
      {showFavoriteButton && (
        <button
          style={styles.favoriteButton}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleFavorite(id);
          }}
          aria-label="Toggle favorite"
        >
          <Heart
            size={16}
            fill={isFavorite ? Colors.error : 'transparent'}
            color={Colors.surface}
            strokeWidth={2}
          />
        </button>
      )}
    </div>
  );
}

/**
 * Calculates grid positioning and aspect ratios for different tile sizes
 *
 * Grid Configuration:
 * - Container width: responsive (100vw, but with padding)
 * - Columns: 3 equal-width columns
 * - Gap: 4px (defined in MasonryGrid component)
 * - Single cell aspect ratio: 1:1 (square)
 *
 * Aspect Ratio Calculations:
 * For single-row items (1x1, 2x1, 3x1):
 *   aspect-ratio = columns / 1
 *
 * For multi-row items, we must account for the gap between rows:
 *   Example for [1x2] (1 column, 2 rows):
 *   - Row height = column width (since [1x1] is square)
 *   - Total height = (2 × row height) + (1 × gap)
 *   - With gap=4px and typical row_height≈110px:
 *     height = 2×110 + 4 = 224px
 *   - aspect-ratio = width / height = 110 / 224 ≈ 1 / 2.036 ≈ 1 / 2.08
 *
 * To adjust for different gap values:
 *   [1x2]: 1 / (2 + gap/cellWidth)
 *   [2x2]: 2 / (2 + gap/cellWidth)  [but typically 1:1 for visual square]
 *   [3x2]: 3 / (2 + gap/cellWidth)
 */
function getSizeStyles(size: GridSize): React.CSSProperties {
  const columnMap: Record<GridSize, number> = {
    '1x1': 1,
    '2x1': 2,
    '3x1': 3,
    '1x2': 1,
    '2x2': 2,
    '3x2': 3,
  };

  const rowMap: Record<GridSize, number> = {
    '1x1': 1,
    '2x1': 1,
    '3x1': 1,
    '1x2': 2,
    '2x2': 2,
    '3x2': 2,
  };

  /**
   * Aspect ratios for each tile size
   *
   * Ratio format: width / height
   * - [1x1]: Square (1 col × 1 row)
   * - [2x1]: Rectangle (2 cols × 1 row) = 2:1
   * - [3x1]: Rectangle (3 cols × 1 row) = 3:1
   * - [1x2]: Tall (1 col × 2 rows with gap) = 1:2.08
   *   Calculation: height = 2×cellHeight + 4px gap ≈ 2.08×cellHeight
   * - [2x2]: Square (2 cols × 2 rows with gap) = 1:1
   *   Stays square despite gap (visual balance)
   * - [3x2]: Rectangle (3 cols × 2 rows with gap) = 3:2
   */
  const aspectRatioMap: Record<GridSize, string> = {
    '1x1': '1 / 1',     // Square: 1 column, 1 row
    '2x1': '2 / 1',     // Wide: 2 columns, 1 row
    '3x1': '3 / 1',     // Very wide: 3 columns, 1 row
    '1x2': '1 / 2.08',  // Tall: 1 column, 2 rows + gap (see formula above)
    '2x2': '1 / 1',     // Square: 2 columns, 2 rows (maintains visual square despite gap)
    '3x2': '3 / 2',     // Landscape: 3 columns, 2 rows
  };

  return {
    gridColumn: `span ${columnMap[size]}`,
    gridRow: `span ${rowMap[size]}`,
    aspectRatio: aspectRatioMap[size],
  } as React.CSSProperties;
}

const styles = {
  tile: {
    position: 'relative' as const,
    borderRadius: '12px',
    overflow: 'hidden',
    backgroundColor: Colors.surface,
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.05)',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
  } as React.CSSProperties,

  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
    display: 'block',
    transition: 'transform 0.3s ease, opacity 0.2s ease',
  } as React.CSSProperties,

  overlay: {
    position: 'absolute' as const,
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0)',
    display: 'flex',
    alignItems: 'flex-end',
    padding: '12px',
    transition: 'background-color 0.2s ease',
  } as React.CSSProperties,

  title: {
    fontSize: '14px',
    fontWeight: '600',
    color: Colors.surface,
    margin: 0,
    lineHeight: '1.2',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
  } as React.CSSProperties,

  favoriteButton: {
    position: 'absolute' as const,
    top: '8px',
    right: '8px',
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    border: 'none',
    borderRadius: '16px',
    padding: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    zIndex: 20,
  } as React.CSSProperties,
};
